/**
 * 日次計算エンジン（ガイドライン v7 §8 / 運用マニュアル §3.2）
 *
 * v7 の要点：
 * - みなし労働は「8時間頭打ち＋スライド補填」。実作業が8時間に満たない日だけ、
 *   移動原資から不足分を実働へ補填する（8:30／18:00 のアンカー判定は廃止）。
 * - みなし通勤控除（片道1時間）とみなし補填は平日のみ。休日は実時間で計算する。
 * - 休憩は「合計（⑦）」を入力し、実働から控除する分（X列）を固定休憩帯との重なりで
 *   自動算出（W列）する。残り（⑦−X）は移動から控除する。
 *
 * すべての計算は分単位の整数で行う（ガイドライン §6.4「1分単位・端数処理なし」）。
 * 深夜時間のみ、休憩の按分控除により小数分を含みうる。
 */
import { BREAK_RULE, DEEMED_COMMUTE_MINUTES, FIXED_BREAK_BANDS, MEAL_ALLOWANCE_THRESHOLD_MINUTES, NIGHT_BANDS, PRESCRIBED_DAILY_MINUTES, } from './constants.js';
import { overlapMinutes } from './time.js';
import { HOLIDAY_DAY_TYPES, WORKED_TYPES, } from './types.js';
/**
 * 入力パターンを判定する（ガイドライン §7.3）。
 * 朝は直行／出社のいずれか、帰りは直帰／退社のいずれかで決まる。
 */
export function detectPattern(input) {
    const morning = input.directStart !== null ? 'direct' : input.officeIn !== null ? 'office' : null;
    const evening = input.directHome !== null ? 'home' : input.officeOut !== null ? 'office' : null;
    const hasWorkTimes = input.workStart !== null && input.workEnd !== null;
    if (!hasWorkTimes && morning === 'direct' && evening === 'home')
        return 'E';
    if (morning === 'office' && evening === 'office')
        return 'A';
    if (morning === 'office' && evening === 'home')
        return 'B';
    if (morning === 'direct' && evening === 'home')
        return 'C';
    if (morning === 'direct' && evening === 'office')
        return 'D';
    return null;
}
/**
 * W列 休憩自動：固定休憩時間帯（10:00〜10:15／12:00〜13:00／15:00〜15:15）と
 * 始業〜終業の重なり（分）。平日のみ算出する。
 */
export function computeBreakAuto(input, isWeekday) {
    if (!isWeekday)
        return 0;
    if (input.workStart === null || input.workEnd === null)
        return 0;
    return overlapMinutes(input.workStart, input.workEnd, FIXED_BREAK_BANDS);
}
/**
 * X列 実働控除採用値：
 * - 平日：V列（修正）があればV列、なければW列（自動）。いずれも⑦休憩合計が上限。
 * - 休日：⑦休憩合計の全量（自動算出は行わない）。
 * - 実働がない日（移動のみ）：0（休憩は全量を移動から控除する）。
 */
export function computeBreakWorkDeduct(input, isWeekday, breakAuto) {
    if (input.workStart === null || input.workEnd === null)
        return 0;
    const breakTotal = input.breakTotalMinutes ?? 0;
    if (!isWeekday)
        return breakTotal;
    const adopted = input.breakOverrideMinutes ?? breakAuto;
    return Math.max(0, Math.min(adopted, breakTotal));
}
/**
 * 深夜時間（ガイドライン §9.1・§12.4、GAS nightHours_ と同一ロジック）。
 *
 * - 実働区間（始業〜終業）の深夜帯重なり。休憩をいつ取ったか分からない日
 *   （V列で手修正した日と、⑦休憩合計の全量を実働から控除する休日）は、
 *   実働に対する深夜割合で按分控除する。
 *   平日で自動判定（W列採用）の日は、固定休憩帯が深夜帯と重ならないため控除しない。
 * - 移動は、みなし通勤控除後の支給対象分の深夜帯重なりから、移動中休憩（⑦−X）を按分控除する。
 * - 移動のみの日は、支給対象分（往復のみなし通勤控除・休憩控除後）を上限に深夜帯の重なりを取る。
 */
export function computeNightMinutes(input, isWeekday, breakWorkDeduct) {
    const commuteDeduct = isWeekday ? DEEMED_COMMUTE_MINUTES : 0;
    const breakTotal = input.breakTotalMinutes ?? 0;
    const night = (a, b) => overlapMinutes(a, b, NIGHT_BANDS);
    if (input.workStart !== null && input.workEnd !== null) {
        let total = 0;
        let workNight = night(input.workStart, input.workEnd);
        const span = input.workEnd - input.workStart;
        // 休日は W を使わず⑦の全量を実働から控除するため、平日の自動判定と違って
        // 「固定休憩帯が深夜と重ならない」とは言えない。按分控除の対象に含める
        const breakTimingUnknown = input.breakOverrideMinutes !== null || !isWeekday;
        if (workNight > 0 && breakTimingUnknown && breakWorkDeduct > 0 && span > 0) {
            workNight = Math.max(0, workNight - breakWorkDeduct * (workNight / span));
        }
        total += workNight;
        let goPaid = 0;
        let backPaid = 0;
        let goNight = 0;
        let backNight = 0;
        if (input.directStart !== null) {
            goPaid = Math.max(0, input.workStart - input.directStart - commuteDeduct);
            goNight = Math.min(goPaid, night(input.directStart, input.workStart));
        }
        if (input.directHome !== null) {
            backPaid = Math.max(0, input.directHome - input.workEnd - commuteDeduct);
            backNight = Math.min(backPaid, night(input.workEnd, input.directHome));
        }
        let travelNight = goNight + backNight;
        const travelBreak = Math.max(0, breakTotal - breakWorkDeduct);
        const paidTotal = goPaid + backPaid;
        if (travelNight > 0 && travelBreak > 0 && paidTotal > 0) {
            travelNight = (travelNight * Math.max(0, paidTotal - travelBreak)) / paidTotal;
        }
        total += travelNight;
        return total;
    }
    if (input.directStart !== null && input.directHome !== null) {
        const paid = Math.max(0, input.directHome - input.directStart - commuteDeduct * 2 - breakTotal);
        return Math.min(paid, night(input.directStart, input.directHome));
    }
    return 0;
}
/**
 * 1日分の自動計算を行う。
 *
 * 計算順序（計算根拠画面と同じ）：
 * ① 休憩の振り分け（W列・X列） → ② 実作業A → ③ 移動原資Z
 * → ④ みなし補填M → ⑤ 実働N・移動O・稼働P → ⑥ 日残業R・各種判定
 */
export function computeDaily(input) {
    const isWeekday = input.dayType === '平日';
    const isLegalHoliday = input.dayType === '法定休日';
    const isHolidayType = HOLIDAY_DAY_TYPES.includes(input.dayType);
    const isWorked = WORKED_TYPES.includes(input.workType);
    const pattern = detectPattern(input);
    const commuteDeduct = isWeekday ? DEEMED_COMMUTE_MINUTES : 0;
    const breakTotal = input.breakTotalMinutes ?? 0;
    // ① 休憩の振り分け
    const breakAutoMinutes = computeBreakAuto(input, isWeekday);
    const breakWorkDeductMinutes = computeBreakWorkDeduct(input, isWeekday, breakAutoMinutes);
    const breakTravelDeductMinutes = Math.max(0, breakTotal - breakWorkDeductMinutes);
    const hasWorkTimes = input.workStart !== null && input.workEnd !== null;
    const isTravelOnly = !hasWorkTimes && input.directStart !== null && input.directHome !== null;
    // ② 実作業A（終業 − 始業 − 実働控除休憩）
    const actualWorkMinutes = hasWorkTimes
        ? Math.max(0, input.workEnd - input.workStart - breakWorkDeductMinutes)
        : 0;
    // ③ 移動原資Z
    let travelPoolMinutes = 0;
    if (isTravelOnly) {
        // 移動のみの日（パターンE）：直帰 − 直行 − みなし通勤往復 − 休憩全量
        travelPoolMinutes = Math.max(0, input.directHome - input.directStart - commuteDeduct * 2 - breakTotal);
    }
    else if (hasWorkTimes) {
        const go = input.directStart !== null
            ? Math.max(0, input.workStart - input.directStart - commuteDeduct)
            : 0;
        const back = input.directHome !== null
            ? Math.max(0, input.directHome - input.workEnd - commuteDeduct)
            : 0;
        travelPoolMinutes = Math.max(0, go + back - breakTravelDeductMinutes);
    }
    // ④ みなし補填M（平日かつ始業・終業がある日のみ。不足分を原資の範囲で補填）
    const deemedFillMinutes = isWeekday && hasWorkTimes
        ? Math.min(Math.max(0, PRESCRIBED_DAILY_MINUTES - actualWorkMinutes), travelPoolMinutes)
        : 0;
    // ⑤ 実働N・移動O・稼働P
    const workMinutes = actualWorkMinutes + deemedFillMinutes;
    const travelMinutes = travelPoolMinutes - deemedFillMinutes;
    const totalMinutes = workMinutes + travelMinutes;
    // ⑥ 判定・計上
    const dailyOvertimeMinutes = isLegalHoliday
        ? 0
        : Math.max(0, totalMinutes - PRESCRIBED_DAILY_MINUTES);
    const nightMinutes = computeNightMinutes(input, isWeekday, breakWorkDeductMinutes);
    const legalHolidayMinutes = isLegalHoliday && isWorked ? totalMinutes : 0;
    const prescribedHolidayMinutes = !isLegalHoliday && isHolidayType && isWorked
        ? Math.min(totalMinutes, PRESCRIBED_DAILY_MINUTES)
        : 0;
    const holidayOver8Minutes = input.workType === '休日出勤' ? Math.max(0, totalMinutes - PRESCRIBED_DAILY_MINUTES) : 0;
    const mealAllowance = isTravelOnly
        ? travelMinutes >= MEAL_ALLOWANCE_THRESHOLD_MINUTES
        : workMinutes >= MEAL_ALLOWANCE_THRESHOLD_MINUTES;
    const requiredBreak = workMinutes > BREAK_RULE.over8hThresholdMinutes
        ? BREAK_RULE.over8hRequiredMinutes
        : workMinutes > BREAK_RULE.over6hThresholdMinutes
            ? BREAK_RULE.over6hRequiredMinutes
            : 0;
    const breakShortage = breakWorkDeductMinutes < requiredBreak;
    const spanStart = firstDeparture(input);
    const spanEnd = lastArrival(input);
    const attendanceSpanMinutes = spanStart !== null && spanEnd !== null ? Math.max(0, spanEnd - spanStart) : 0;
    return {
        pattern,
        isWeekday,
        isWorked,
        breakAutoMinutes,
        breakWorkDeductMinutes,
        breakTravelDeductMinutes,
        actualWorkMinutes,
        travelPoolMinutes,
        deemedFillMinutes,
        workMinutes,
        travelMinutes,
        totalMinutes,
        dailyOvertimeMinutes,
        nightMinutes,
        legalHolidayMinutes,
        prescribedHolidayMinutes,
        holidayOver8Minutes,
        attendanceSpanMinutes,
        mealAllowance,
        breakShortage,
    };
}
/** その日の最初の出発時刻（①直行 または ②出社。なければ③始業） */
export function firstDeparture(input) {
    return input.directStart ?? input.officeIn ?? input.workStart;
}
/** その日の最後の到着時刻（⑥直帰 または ⑤退社。なければ④終業） */
export function lastArrival(input) {
    return input.directHome ?? input.officeOut ?? input.workEnd;
}
/** 空の日報入力を作る（画面の初期値・テストの土台） */
export function emptyDailyInput(date, dayType = '平日') {
    const isWeekday = dayType === '平日';
    return {
        date,
        dayType,
        workType: isWeekday ? '出勤' : '休日出勤',
        directStart: null,
        officeIn: null,
        workStart: null,
        workEnd: null,
        officeOut: null,
        directHome: null,
        breakTotalMinutes: null,
        hourlyLeaveHours: 0,
        breakOverrideMinutes: null,
        holidayWorkTreatment: null,
        taskNote: '',
        siteName: '',
        remarks: '',
    };
}
//# sourceMappingURL=daily.js.map